import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useApproveRejectComment } from '@/api/groups/approve-reject-comment'
import { getGroups } from '@/api/groups/get-groups'
import { useGetPendingComments } from '@/api/groups/get-pending-comments'
import { useGetPostById } from '@/api/groups/get-post-by-id'
import PostFullCard from '@/components/Groups/GroupPostsDashboard/PostFullCard/PostFullCard'
import { TableGroup } from '@/components/layout/table-group'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { Select, useSelectOptions } from '@/components/ui/select'
import { SortOrder, TableColumn } from '@/components/ui/table'
import { useFilters, useSearchParams } from '@/hooks'
import { Comment } from '@/models/features/comments.models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectUserData } from '@/reducers/userDataReducer'

import {
  commentsInitialFilters,
  commentsQueryFiltersSchema,
  getCommentsColumns,
} from './comments.constants'

interface SelectedRow {
  groupId: number
  postId: number
  commentId: number
}

export const CommentsPage = () => {
  const { t } = useTranslation(['groups'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const { mutate: approveRejectComment, isPending } = useApproveRejectComment()

  const user = useSelector(selectUserData)

  const [selectedRow, setSelectedRow] = useState<SelectedRow | null>(null)

  const { data } = useGetPostById({ groupId: selectedRow?.groupId, postId: selectedRow?.postId })

  const searchParams = useSearchParams({
    initialState: commentsInitialFilters,
    schema: commentsQueryFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: commentsInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const limit = filters.getFilter('limit') ?? DEFAULT_LIMIT
  const skip = filters.getFilter('skip') ?? 0
  const groupId = filters.getFilter('groupId')
  const sortOrder = filters.getFilter('sortOrder')
  const sortBy = filters.getFilter('sortBy')

  const { data: pendingCommentsData, isFetching } = useGetPendingComments({
    buildingId: activeBuilding?.id,
    groupId,
    limit,
    skip,
    sortBy,
    sortOrder,
  })

  const groupsOptions = useSelectOptions(
    {
      query: getGroups,
      requestData: {
        buildingId: activeBuilding?.id,
      },
      transform: (groupsData) => {
        const groups = groupsData || []
        return groups.map((group) => ({
          label: group.name,
          value: group.id.toString(),
        }))
      },
    },
    {
      enabled: !!activeBuilding?.id,
      queryKey: ['groups', activeBuilding?.id],
    },
  )

  const onAproveRejectButtonClick = (comment: Comment, approve: boolean) => {
    approveRejectComment({
      approve,
      commentId: comment.id,
      groupId: comment.group.id,
      postId: comment.postId.id,
    })
  }

  const commentsColumns: TableColumn<Comment>[] = getCommentsColumns({
    isPending,
    onAproveRejectButtonClick,
    t,
  })

  const handleParentChecklistChange = (groupId: string) => {
    filters.setFilter('groupId', groupId)
  }

  const handleSort = (sortBy: string | null, sortOrder: SortOrder | null) => {
    filters.setFilters({
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
    })
  }

  const handleShowPost = (comment: Comment) => {
    setSelectedRow({
      commentId: comment.id,
      groupId: comment.group.id,
      postId: comment.postId.id,
    })
  }

  return (
    <>
      <TableGroup
        headerFiltersHorizontal
        id="comments"
        columns={commentsColumns}
        data={pendingCommentsData?.data || []}
        getRowKey={(comment) => comment.id}
        loading={isFetching}
        emptyNoFilters={{
          description: t('groups:comments.emptyState.description'),
          title: t('groups:comments.emptyState.title'),
        }}
        filters={
          <Select
            {...groupsOptions.selectProps}
            searchable
            size="small"
            value={filters.getFilter('groupId') ?? null}
            type="single"
            placeholder={t('groups:comments.filters.group.placeholder')}
            onChange={handleParentChecklistChange}
            onReset={() => handleParentChecklistChange('')}
          />
        }
        sortBy={filters.getFilter('sortBy')}
        sortOrder={filters.getFilter('sortOrder')}
        rowActions={[
          {
            label: t('groups:comments.rowActions.showPost'),
            onClick: handleShowPost,
            primary: true,
          },
        ]}
        pagination={{
          paginationFilters: filters,
          total: pendingCommentsData?.count ?? 0,
        }}
        onSort={handleSort}
      />
      {!!data && !!selectedRow && (
        <PostFullCard
          readOnlyMode
          isSubmitting={false}
          post={data}
          currentUser={user}
          groupName={data?.groupId.name}
          buildingTimezone={activeBuilding?.timezone}
          isCommentsEnabled={false}
          handleCancel={() => {
            setSelectedRow(null)
          }}
          scrollIntoCommentId={selectedRow.commentId}
          onDeletePost={() => {}}
          onEditPost={() => {}}
          onAddComment={() => {}}
          onLikeDislike={() => {}}
          onManagePostStatus={() => {}}
          onDeleteComment={() => {}}
          onApproveRejectCallback={() => {}}
        />
      )}
    </>
  )
}
